import { useState } from "react";
import { useAuth } from "../components/AuthContext";
import { useLiveData, useActionWithRefresh } from "../hooks/useLiveData";
import { getAllFeatures } from "../services/features";
import { getAllBugs } from "../services/bugs";
import { getAllVersions, deployVersion, getDeployQueue } from "../services/versions";
import { reconcilePipelineState } from "../services/reconciler";
import {
  getPipelineState,
  getRecentExecutions,
  approveStage,
  rejectStage,
  enableGate,
  disableGate,
  stopPipeline,
  retryStage,
  type PipelineStageStatus,
  type PipelineExecution,
} from "../services/pipeline";
import type { Version } from "../types";

export default function Dashboard() {
  const { can } = useAuth();
  const [showDeployModal, setShowDeployModal] = useState(false);

  // Live pipeline state (polls every 8s)
  const pipeline = useLiveData<PipelineStageStatus[]>({
    fetcher: async () => {
      await reconcilePipelineState();
      return getPipelineState();
    },
    interval: 8000,
  });

  // Live deploy queue
  const queue = useLiveData<Version[]>({
    fetcher: getDeployQueue,
    interval: 8000,
  });

  // Deployable versions (created but not yet deployed)
  const deployable = useLiveData<Version[]>({
    fetcher: async () => {
      const versions = await getAllVersions();
      return versions.filter((v) => v.status === "created" || v.status === "failed");
    },
    interval: 15000,
  });

  // Recent executions
  const executions = useLiveData<PipelineExecution[]>({
    fetcher: () => getRecentExecutions(5),
    interval: 10000,
  });

  // Stats
  const stats = useLiveData<{ openFeatures: number; openBugs: number }>({
    fetcher: async () => {
      const [features, bugs] = await Promise.all([getAllFeatures(), getAllBugs()]);
      return {
        openFeatures: features.filter((f) => f.status === "open" || f.status === "in-progress").length,
        openBugs: bugs.filter((b) => b.status === "open" || b.status === "in-progress").length,
      };
    },
    interval: 30000,
  });

  // Refresh all on action
  const refreshAll = async () => {
    await Promise.all([pipeline.refresh(), queue.refresh(), deployable.refresh(), executions.refresh()]);
  };

  const { busy, actionError, execute, clearError } = useActionWithRefresh(refreshAll);

  const stages = pipeline.data || [];
  const currentExecutionId = stages.find((s) => s.latestExecution)?.latestExecution?.pipelineExecutionId || "";

  function getStageType(name: string): "source" | "build" | "deploy" | "approval" {
    if (name.includes("Source")) return "source";
    if (name.includes("Build")) return "build";
    if (name.includes("Approv")) return "approval";
    return "deploy";
  }

  function getStageEnv(name: string): string {
    if (name.includes("Dev")) return "dev";
    if (name.includes("Beta")) return "beta";
    if (name.includes("Prod")) return "prod";
    return "";
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2>POS System Pipeline</h2>
            <p>Deploy versions, control gates, approve promotions.</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            {pipeline.lastUpdated && (
              <span className="live-indicator">
                <span className="live-dot" />
                Live · {pipeline.lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button className="ctrl-btn refresh" onClick={refreshAll} disabled={pipeline.loading}>
              {pipeline.loading ? "⟳" : "↻"}
            </button>
          </div>
        </div>
      </div>

      {(actionError || pipeline.error) && (
        <div className="error-banner">
          ⚠️ {actionError || pipeline.error}
          <button onClick={() => { clearError(); }} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: "1rem" }}>✕</button>
        </div>
      )}

      {/* Deploy Queue */}
      {((queue.data && queue.data.length > 0) || (deployable.data && deployable.data.length > 0)) && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <h3>Deploy Queue</h3>
            {can("pipeline:trigger") && deployable.data && deployable.data.length > 0 && (
              <button className="btn btn-primary" onClick={() => setShowDeployModal(!showDeployModal)}>
                ▶ Deploy Version
              </button>
            )}
          </div>

          {/* Currently deploying */}
          {queue.data && queue.data.length > 0 && (
            <div style={{ marginBottom: "1rem" }}>
              {queue.data.map((v) => (
                <div key={v.id} className="deploy-queue-item deploying">
                  <span className="dq-status">
                    {v.status === "deploying" ? "🔄" : "⏳"}
                  </span>
                  <span className="dq-version">v{v.version}</span>
                  <span className="dq-stage">→ {v.stage}</span>
                  <span className="dq-commits">{v.commits.length} commits</span>
                  {v.pipelineExecutionId && (
                    <code className="dq-exec">{v.pipelineExecutionId.substring(0, 8)}</code>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Deploy modal — pick version to deploy */}
          {showDeployModal && deployable.data && (
            <div style={{ background: "var(--bg)", padding: "1rem", borderRadius: "8px", marginBottom: "1rem" }}>
              <h4 style={{ marginBottom: "0.75rem" }}>Select version to deploy:</h4>
              {deployable.data.map((v) => (
                <div key={v.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0", borderBottom: "1px solid var(--border)" }}>
                  <strong>v{v.version}</strong>
                  <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{v.commits.length} commits</span>
                  <code style={{ fontSize: "0.75rem" }}>{v.targetCommitId?.substring(0, 7)}</code>
                  {v.status === "failed" && <span className="badge badge-danger">failed — retry?</span>}
                  <button
                    className="btn btn-primary"
                    style={{ marginLeft: "auto", padding: "0.25rem 0.75rem", fontSize: "0.8rem" }}
                    onClick={() => {
                      execute(`deploy-${v.id}`, () => deployVersion(v.id));
                      setShowDeployModal(false);
                    }}
                    disabled={busy === `deploy-${v.id}`}
                  >
                    {busy === `deploy-${v.id}` ? "⟳" : "▶"} Deploy
                  </button>
                </div>
              ))}
              {deployable.data.length === 0 && (
                <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>No versions ready to deploy. Create one in Versions.</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Pipeline Card */}
      <div className="pipeline-card">
        <div className="pc-header">
          <div className="pc-title">
            <span className="pc-name">pos-system-deploy</span>
            <span className={`pc-status-pill ${stages.some((s) => s.status === "InProgress") ? "running" : stages.some((s) => s.status === "Failed") ? "failed" : "idle"}`}>
              {stages.some((s) => s.status === "InProgress") ? "Running" : stages.some((s) => s.status === "Failed") ? "Failed" : "Idle"}
            </span>
          </div>
          <div className="pc-controls">
            {can("pipeline:stop") && currentExecutionId && stages.some((s) => s.status === "InProgress") && (
              <button className="ctrl-btn stop" onClick={() => execute("stop", () => stopPipeline(currentExecutionId, "Stopped via DevPortal"))} disabled={busy === "stop"}>
                {busy === "stop" ? "⟳" : "⏹"} Stop
              </button>
            )}
          </div>
        </div>

        <div className="pc-stages">
          {stages.map((stage) => {
            const type = getStageType(stage.stageName);
            const env = getStageEnv(stage.stageName);
            const approvalAction = stage.actionStates.find((a) => a.latestExecution?.token);
            const gateEnabled = stage.inboundTransitionState?.enabled !== false;
            const isFailed = stage.status === "Failed";
            const isActive = stage.status === "InProgress";

            return (
              <div key={stage.stageName} className={`pc-stage ${isActive ? "active" : ""} ${isFailed ? "failed" : ""} ${!gateEnabled ? "gated" : ""}`}>
                <div className="stage-gate">
                  {can("pipeline:gates") && stage.inboundTransitionState !== undefined && (
                    <button
                      className={`gate-toggle ${gateEnabled ? "open" : "closed"}`}
                      onClick={() => execute(`gate-${stage.stageName}`, () => gateEnabled ? disableGate(stage.stageName) : enableGate(stage.stageName))}
                      disabled={busy === `gate-${stage.stageName}`}
                      title={gateEnabled ? "Close gate" : "Open gate"}
                    >
                      {gateEnabled ? "🟢" : "🔴"}
                    </button>
                  )}
                  {!can("pipeline:gates") && stage.inboundTransitionState !== undefined && (
                    <span className={`gate-indicator ${gateEnabled ? "open" : "closed"}`}>
                      {gateEnabled ? "🟢" : "🔴"}
                    </span>
                  )}
                </div>

                <div className="stage-body">
                  <div className="stage-row-header">
                    <span className="stage-row-name">{stage.stageName.replace(/_/g, " ")}</span>
                    <span className={`stage-row-status s-${stage.status.toLowerCase()}`}>{stage.status}</span>
                  </div>

                  <div className="stage-row-actions">
                    {type === "approval" && approvalAction?.latestExecution?.token && can("pipeline:approve-frontend") && (
                      <>
                        <button
                          className="action-btn approve"
                          onClick={() => execute(`approve-${stage.stageName}`, () => approveStage(stage.stageName, approvalAction.actionName, approvalAction.latestExecution!.token!))}
                          disabled={busy === `approve-${stage.stageName}`}
                        >
                          {busy === `approve-${stage.stageName}` ? "⟳" : "✓"} Approve
                        </button>
                        <button
                          className="action-btn reject"
                          onClick={() => execute(`reject-${stage.stageName}`, () => rejectStage(stage.stageName, approvalAction.actionName, approvalAction.latestExecution!.token!))}
                          disabled={busy === `reject-${stage.stageName}`}
                        >
                          ✕ Reject
                        </button>
                      </>
                    )}

                    {isFailed && stage.latestExecution && can("pipeline:rollback") && (
                      <button
                        className="action-btn retry"
                        onClick={() => execute(`retry-${stage.stageName}`, () => retryStage(stage.stageName, stage.latestExecution!.pipelineExecutionId))}
                        disabled={busy === `retry-${stage.stageName}`}
                      >
                        ↻ Retry
                      </button>
                    )}

                    {!gateEnabled && stage.inboundTransitionState?.disabledReason && (
                      <span className="gate-reason">{stage.inboundTransitionState.disabledReason}</span>
                    )}
                  </div>
                </div>

                {env && <span className={`env-badge env-${env}`}>{env}</span>}
              </div>
            );
          })}
        </div>

        {stages.length === 0 && !pipeline.loading && (
          <div className="pc-empty">No pipeline data. Check AWS connection.</div>
        )}
      </div>

      {/* Recent Executions */}
      {executions.data && executions.data.length > 0 && (
        <div className="card" style={{ marginTop: "1.5rem" }}>
          <h3 style={{ marginBottom: "0.75rem" }}>Recent Executions</h3>
          <div style={{ fontSize: "0.85rem" }}>
            {executions.data.map((exec) => (
              <div key={exec.pipelineExecutionId} style={{ display: "flex", gap: "1rem", padding: "0.4rem 0", borderBottom: "1px solid var(--border)", alignItems: "center" }}>
                <span className={`badge ${exec.status === "Succeeded" ? "badge-success" : exec.status === "Failed" ? "badge-danger" : "badge-warning"}`}>
                  {exec.status}
                </span>
                <code>{exec.pipelineExecutionId.substring(0, 12)}</code>
                <span style={{ color: "var(--text-muted)" }}>{new Date(exec.startTime).toLocaleString()}</span>
                {exec.sourceRevisions?.[0] && (
                  <code style={{ marginLeft: "auto", fontSize: "0.75rem" }}>{exec.sourceRevisions[0].revisionId.substring(0, 7)}</code>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="card-grid" style={{ marginTop: "1.5rem" }}>
        <div className="stat-card">
          <div className="label">Open Features</div>
          <div className="value" style={{ color: (stats.data?.openFeatures || 0) > 0 ? "var(--primary)" : "inherit" }}>
            {stats.data?.openFeatures ?? "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Open Bugs</div>
          <div className="value" style={{ color: (stats.data?.openBugs || 0) > 0 ? "var(--danger)" : "inherit" }}>
            {stats.data?.openBugs ?? "—"}
          </div>
        </div>
      </div>
    </div>
  );
}
